import React, { useState, useEffect } from "react";
import { ExpenseCategory } from "@/types/expense";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ExpenseTab } from "@/components/tabs/ExpenseTab";
import { AnalyticsTab } from "@/components/tabs/AnalyticsTab";
import { HistoryTab } from "@/components/tabs/HistoryTab";
import { motion } from "framer-motion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, BarChart3, Users } from "lucide-react";
import { useExpenses } from "@/hooks/useExpenses";
import { useRecentItems } from "@/hooks/useRecentItems";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

interface TripContentProps {
  tripParticipants: string[];
  onAddExpense: (expense: any) => void;
  onDeleteExpense: (id: string) => void;
  budget?: number;
}

export const TripContent: React.FC<TripContentProps> = ({ 
  tripParticipants,
  onAddExpense,
  onDeleteExpense,
  budget = 5000
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ExpenseCategory | "all">("all");
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: undefined,
    to: undefined,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const { recentItems: recentExpenses } = useRecentItems('recentExpenses', 10);
  const { expenses } = useExpenses();

  // Calculate total expenses
  const totalExpenses = expenses.reduce((sum, exp) => sum + Number(exp.amount), 0);
  const budgetProgress = (totalExpenses / budget) * 100;

  // Check budget threshold
  useEffect(() => {
    if (totalExpenses >= budget * 0.8 && totalExpenses < budget) {
      toast.warning(`You've used ${Math.round(budgetProgress)}% of your budget!`);
    } else if (totalExpenses >= budget) {
      toast.error(`You've exceeded your budget of ₹${budget}!`);
    }
  }, [totalExpenses, budget, budgetProgress]);

  return (
    <div className="space-y-4">
      <div className="p-4 bg-gradient-card rounded-lg">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Budget Usage</span>
            <span>₹{totalExpenses.toLocaleString()} / ₹{budget.toLocaleString()}</span>
          </div>
          <Progress value={budgetProgress} className="h-2" />
        </div>
      </div>

      <Tabs defaultValue="expenses" className="w-full">
        <ScrollArea className="w-full max-w-full overflow-x-auto">
          <TabsList className="grid w-full grid-cols-3 mb-4 md:mb-8 bg-gradient-card p-1 rounded-lg min-w-[300px]">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <TabsTrigger value="expenses" className="w-full flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span className="hidden md:inline">Expenses</span>
                <span className="md:hidden">Exp</span>
              </TabsTrigger>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <TabsTrigger value="analytics" className="w-full flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                <span className="hidden md:inline">Analytics</span>
                <span className="md:hidden">Ana</span>
              </TabsTrigger>
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <TabsTrigger value="history" className="w-full flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span className="hidden md:inline">History</span>
                <span className="md:hidden">His</span>
              </TabsTrigger>
            </motion.div>
          </TabsList>
        </ScrollArea>
        
        <ScrollArea className="h-[calc(100vh-16rem)] md:h-[calc(100vh-20rem)] rounded-lg">
          <TabsContent value="expenses" className="mt-0 min-w-[300px]">
            <ExpenseTab
              expenses={expenses}
              onAddExpense={onAddExpense}
              onDeleteExpense={onDeleteExpense}
              selectedCategory={selectedCategory}
              dateRange={dateRange}
              searchQuery={searchQuery}
              onCategoryChange={setSelectedCategory}
              onDateRangeChange={setDateRange}
              onSearchChange={setSearchQuery}
              tripParticipants={tripParticipants}
              budget={budget}
              totalExpenses={totalExpenses}
            />
          </TabsContent>
          <TabsContent value="analytics" className="mt-0 min-w-[300px]">
            <AnalyticsTab
              expenses={expenses}
              recentExpenses={recentExpenses}
              tripParticipants={tripParticipants}
              budget={budget}
            />
          </TabsContent>
          <TabsContent value="history" className="mt-0 min-w-[300px]">
            <HistoryTab 
              expenses={expenses}
              tripParticipants={tripParticipants}
            />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );
};